import React, { useState, useEffect, useRef,useImperativeHandle } from 'react'
import styled from 'styled-components'

const PlayerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 10px ${props => props.alignLeft ? '0' : 'auto'};
  width: 100%;
  position: relative;
  max-width: 650px;
  align-items: ${props => props.alignLeft ? 'flex-start' : 'center'};
`

const PlayerHeader = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #111827;
  margin-bottom: 10px;
  text-align: center;
  width: 100%;
`

const PlayerControlsWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: ${props => props.alignLeft ? 'flex-start' : 'center'};
  padding-left: 28px;
  box-sizing: border-box;
`

const PlayerControls = styled.div`
  display: flex;
  align-items: center;

  background-color: white;
  border-radius: 32px;
  padding: 6px 22px 6px 22px; /* reduced left padding since button sits on top */
  width: 100%;
  max-width: 100%;
  height: 37px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  margin-left: 0;
  border: 3px solid #3249D7;

  position: relative;
  z-index: 0; /* ensure border stays below the button */
  overflow: visible;
`

const PlayButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  width: 55px;
  height: 55px;
  flex-shrink: 0;
  background-color: #3249D7;
  border-radius: 50%;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 30;
  box-shadow: 0 4px 12px rgba(50,73,215,0.18);

  &:disabled {
    background-color: #bdbdbd; /* grey background */
    color: #757575;            /* grey icon */
    cursor: not-allowed;
  }

  svg {
    width: 26px;
    height: 26px;
  }
`

const ProgressBar = styled.div`
  flex-grow: 1;
  height: 3px;
  background: #E5E7EB;
  border-radius: 2.5px;
  position: relative;
`

const Progress = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #3249D7;
  border-radius: 2.5px;
  width: ${props => props.width}%;
`

const TimeDisplay = styled.span`
  font-size: 15px;
  color: #000000;
  font-weight: 600;
  min-width: 60px;
  text-align: right;
`

const HiddenAudio = styled.audio`
  display: none;
`

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 5v14l11-7z" fill="currentColor" />
  </svg>
)



const AudioPlayer = React.forwardRef(({
  url,
  onAudioComplete,
  isAlreadyCompleted,
  initialPosition = 0,
  onTimeUpdate,
  showHeader = true,
  alignLeft = false,
  allowReplay = false, 
  disabled = false,
}, ref) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState(initialPosition)
  const [duration, setDuration] = useState(0)
  const [isCompleted, setIsCompleted] = useState(isAlreadyCompleted || false)
  const [audioError, setAudioError] = useState(null)
  const [isBuffering, setIsBuffering] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const audioRef = useRef(null)
  const hasSetInitialPosition = useRef(false)
  const stallTimerRef = useRef(null)

  // Slow-connection guard: if the audio is still buffering after this long,
  // surface the Retry control instead of leaving the student with a frozen UI.
  const STALL_HINT_MS = 12000

  const clearStallTimer = () => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current)
      stallTimerRef.current = null
    }
  }

  const armStallTimer = () => {
    clearStallTimer()
    stallTimerRef.current = setTimeout(() => {
      // Only nag if we still haven't started playing.
      setAudioError(prev =>
        prev || 'Audio is taking longer than usual on your connection. Tap "Retry audio".'
      )
    }, STALL_HINT_MS)
  }

  // Re-fetch ONLY this audio object. The <audio> src is the presigned URL that
  // points directly at the single audio file in object storage, so load()
  // re-downloads just that clip — it does NOT refetch questions or anything else.
  const retryAudio = async () => {
    const el = audioRef.current
    if (!el) return
    clearStallTimer()
    setAudioError(null)
    setRetrying(true)
    setIsBuffering(true)
    try {
      el.load()
      armStallTimer()
      await el.play()
      setIsPlaying(true)
      setIsBuffering(false)
      clearStallTimer()
    } catch (e) {
      setIsPlaying(false)
      setIsBuffering(false)
      setAudioError('Could not load the audio. Please check your connection and tap "Retry audio".')
    } finally {
      setRetrying(false)
    }
  }

  // Update isCompleted if the prop changes
  useEffect(() => {
    if (isAlreadyCompleted) {
      setIsCompleted(true);
    }
  }, [isAlreadyCompleted]);
 useImperativeHandle(ref, () => ({
  play: () => {
    if (audioRef.current) {
      setIsPlaying(true); // <-- Add this line
      return audioRef.current.play();
    }
  }
}));
  useEffect(() => {
    // Reset states when URL changes
    setProgress(0)
    setAudioError(null)
    setIsBuffering(false)
    clearStallTimer()

    // Don't reset isCompleted here, as it should persist

    if (!url) {
      console.warn("No audio URL provided");
      return;
    }
  }, [url]);

  // Clear any pending stall timer when the player unmounts.
  useEffect(() => () => clearStallTimer(), []);

  // Set initial position when audio is loaded
  useEffect(() => {
    if (audioRef.current && initialPosition > 0 && !hasSetInitialPosition.current) {
      const setPosition = () => {
        if (audioRef.current.readyState >= 2) {
          audioRef.current.currentTime = initialPosition;
          setCurrentTime(initialPosition);
          hasSetInitialPosition.current = true;
        } else {
          setTimeout(setPosition, 100);
        }
      };

      setPosition();
    }
  }, [initialPosition]);

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const audioDuration = audioRef.current.duration;
      setDuration(audioDuration);

      // Try setting initial position again after metadata is loaded
      if (initialPosition > 0 && !hasSetInitialPosition.current) {
        audioRef.current.currentTime = initialPosition;
        setCurrentTime(initialPosition);
        hasSetInitialPosition.current = true;
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const audioCurrentTime = audioRef.current.currentTime;
      const audioDuration = audioRef.current.duration || 0;
      setCurrentTime(audioCurrentTime);

      if (audioDuration > 0) {
        const progressValue = (audioCurrentTime / audioDuration) * 100;
        setProgress(progressValue);
      }

      // Report time back to parent component
      if (onTimeUpdate && typeof onTimeUpdate === 'function') {
        onTimeUpdate(audioCurrentTime);
      }
    }
  };

  const handleEnded = () => {
    // //console.log("Audio playback ended");
    setIsPlaying(false);
    setProgress(100);
    setIsCompleted(true);

    if (onAudioComplete && typeof onAudioComplete === 'function') {
      // //console.log("Calling onAudioComplete");
      onAudioComplete();
    }
  };

  const handleError = (e) => {
    console.error("Audio error:", e);
    const errorMessage = e.target.error ? `Error: ${e.target.error.code} - ${e.target.error.message}` : 'Unknown error';
    console.error(errorMessage);
    setIsBuffering(false);
    clearStallTimer();
    // Keep the message student-friendly; the Retry control handles recovery.
    setAudioError('Could not load the audio. Please check your connection and tap "Retry audio".');
    setIsPlaying(false);
  };

  // Buffering lifecycle — drives the "Loading audio…" hint and the slow-connection timer.
  const handleWaiting = () => {
    setIsBuffering(true);
    armStallTimer();
  };

  const handlePlaying = () => {
    setIsBuffering(false);
    clearStallTimer();
  };

  const handleCanPlay = () => {
    setIsBuffering(false);
  };

  const togglePlay = () => {
    if (!audioRef.current || disabled) return;
    // Only allow replay if allowReplay is true or not completed yet
    if (isCompleted && !allowReplay) return;

    if (!isPlaying) {
      setAudioError(null);
      setIsBuffering(true);
      armStallTimer();
      try {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
              setIsBuffering(false);
              clearStallTimer();
            })
            .catch(error => {
              // Slow/aborted load — surface the inline Retry control instead of
              // an alert; retry re-fetches only this audio clip.
              setIsPlaying(false);
              setIsBuffering(false);
              clearStallTimer();
              setAudioError('Could not load the audio. Please check your connection and tap "Retry audio".');
            });
        } else {
          setIsPlaying(true);
          setIsBuffering(false);
          clearStallTimer();
        }
      } catch (e) {
        setIsBuffering(false);
        clearStallTimer();
        setAudioError('Could not play the audio. Please tap "Retry audio".');
      }
    }
    // REMOVE PAUSE LOGIC: Do not allow pausing
  };
  const formatTime = seconds => {
    if (isNaN(seconds) || seconds < 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatReverseTime = (current, total) => {
    if (isNaN(current) || isNaN(total) || current < 0 || total < 0) return "0:00";
    const remaining = total - current;
    const mins = Math.floor(remaining / 60);
    const secs = Math.floor(remaining % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlayPause = () => {
    if (audioRef.current.paused) {
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  return (
    <PlayerContainer alignLeft={alignLeft}>
      {showHeader && (
        <PlayerHeader>
          Audio Player
        </PlayerHeader>
      )}
      <HiddenAudio
        ref={audioRef}
        src={url}
        preload="auto"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onError={handleError}
        onWaiting={handleWaiting}
        onStalled={handleWaiting}
        onPlaying={handlePlaying}
        onCanPlay={handleCanPlay}
      />
      <PlayerControlsWrapper alignLeft={alignLeft}>
        {/* move the button outside of the bordered PlayerControls so it can fully sit on top */}
        <PlayButton
          onClick={togglePlay}
          disabled={disabled || !url || (isCompleted && !allowReplay) || isPlaying}
          type="button"
        >
          <PlayIcon style={isPlaying ? { opacity: 1 } : undefined} />
        </PlayButton>

        <PlayerControls>
          <ProgressBar>
            <Progress width={progress} />
          </ProgressBar>
          <TimeDisplay>
            {formatReverseTime(currentTime, duration)}
          </TimeDisplay>
        </PlayerControls>
      </PlayerControlsWrapper>
      {isBuffering && !audioError && (
        <div style={{ color: '#555', fontSize: '14px', marginTop: '5px' }}>
          Loading audio…
        </div>
      )}

      {audioError && (
        <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', alignItems: alignLeft ? 'flex-start' : 'center', gap: '6px' }}>
          <div style={{ color: 'red', fontSize: '14px' }}>
            {audioError}
          </div>
          {url && (
            <button
              type="button"
              onClick={retryAudio}
              disabled={retrying}
              style={{
                padding: '6px 18px',
                borderRadius: '20px',
                border: '2px solid #3249D7',
                background: '#fff',
                color: '#3249D7',
                fontWeight: 600,
                fontSize: '14px',
                cursor: retrying ? 'not-allowed' : 'pointer',
                opacity: retrying ? 0.6 : 1,
              }}
            >
              {retrying ? 'Retrying…' : 'Retry audio'}
            </button>
          )}
        </div>
      )}

      {isCompleted && (
        <div style={{ color: '#777', fontSize: '14px', marginTop: '5px' }}>
          You've completed this audio.
        </div>
      )}
    </PlayerContainer>
  );
});

export default AudioPlayer;
